import React, { useState } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { getIn } from 'formik';
import { IconButton, InputAdornment, Tooltip } from '@mui/material';
import { Visibility, VisibilityOff, Info as InfoIcon, Edit as EditIcon } from '@mui/icons-material';
import TextFieldStyled from './TextField.styled';

const TextField = ({
  type,
  step,
  attrs,
  label,
  margin,
  format,
  onFocus,
  variant,
  editable,
  readOnly,
  decimals,
  required,
  disabled,
  fullWidth,
  handleBlur,
  inputProps,
  asyncErrors,
  placeholder,
  disabledText,
  endAdornment,
  startAdornment,
  inputProperties,
  tooltip,
  editIcon,
  onChageEditIcon,
  field: { id, name, value, onChange, onBlur },
  form: { errors, touched, setFieldValue },
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [focus, setFocus] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const endADornment = () => {
    if (type === 'password') {
      return renderPasswordIcon();
    }
  };

  const handleTooltipClose = () => {
    setOpenTooltip(false);
  };

  const handleTooltipOpen = () => {
    setOpenTooltip(!openTooltip);
  };

  const renderPasswordIcon = () => {
    return (
      <InputAdornment position="end">
        {editIcon && (
          <IconButton aria-label="enable input field">
            <EditIcon onClick={onChageEditIcon} />
          </IconButton>
        )}
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
        {tooltip && (
          <Tooltip onClose={handleTooltipClose} open={openTooltip} title={tooltip}>
            <InfoIcon onClick={handleTooltipOpen} />
          </Tooltip>
        )}
      </InputAdornment>
    );
  };

  const inputType = () => {
    if (type === 'password') {
      return showPassword ? 'text' : 'password';
    } else {
      return type;
    }
  };

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (getIn(errors, name) && getIn(touched, name)) {
      return getIn(errors, name);
    }
  };

  const handleError = () => {
    if (asyncErrors) {
      return true;
    } else if (getIn(errors, name) && getIn(touched, name)) {
      return true;
    }
  };

  const formatValue = (value) => {
    switch (format) {
      case 'price':
        if (value) return parseFloat(value).toFixed(decimals);
        return value;
      default:
        return value;
    }
  };

  if (editable) {
    return (
      <TextFieldStyled
        id={id || name}
        step={step}
        name={name}
        label={label}
        placeholder={placeholder}
        onFocus={() => {
          if (onFocus) onFocus();
          setFocus(true);
        }}
        // add default text when disableText field is true and not value exists
        value={
          disabledText && !value ? i18n.t('common.no_data') : type === 'text' ? value || '' : value
        }
        margin={margin}
        onBlur={(e) => {
          if (onBlur) onBlur(e);
          if (format) {
            setFieldValue(name, formatValue(value));
          }
          if (handleBlur) handleBlur();
          setFocus(false);
        }}
        variant={variant}
        type={disabledText ? 'text' : inputType()}
        onChange={onChange}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        inputProps={{ ...attrs }}
        InputProps={{
          readOnly: readOnly,
          endAdornment: endADornment(getIn(errors, name) && getIn(touched, name)) || endAdornment,
          startAdornment: startAdornment,
          ...inputProps,
        }}
        InputLabelProps={
          inputProperties || {
            shrink: value || value === 0 || focus || disabledText ? true : false,
          }
        }
        error={handleError()}
        helperText={handleShowError()}
        {...props}
      />
    );
  } else {
    return <b>{value}</b>;
  }
};

TextField.propTypes = {
  step: PropTypes.string,
  type: PropTypes.string,
  handleBlur: PropTypes.func,
  onFocus: PropTypes.func,
  format: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  tooltip: PropTypes.string,
  field: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  inputProps: PropTypes.object,
  attrs: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  margin: PropTypes.oneOf(['none', 'normal', 'dense']),
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
  editable: PropTypes.bool,
  decimals: PropTypes.number,
  asyncErrors: PropTypes.array,
  disabledText: PropTypes.bool,
  endAdornment: PropTypes.func,
  inputProperties: PropTypes.object,
  startAdornment: PropTypes.func,
  editIcon: PropTypes.bool,
  onChageEditIcon: PropTypes.func,
};

TextField.defaultProps = {
  step: '',
  format: '',
  decimals: 2,
  type: 'text',
  inputProps: {},
  attrs: {},
  required: true,
  fullWidth: true,
  readOnly: false,
  disabled: false,
  margin: 'normal',
  variant: 'standard',
  editable: true,
};

export default TextField;
